import { createContext, ReactElement, ReactNode, useEffect, useMemo } from 'react';
import axiosLibrary, { AxiosRequestConfig, AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import { RequestOptionsOverride } from 'shell/hooks/use-http-fetch/stateless/type';

/**
 * Default request options.
 */
export const defaultOptions: RequestOptionsOverride = {
  method: 'GET',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
};

export const AxiosIntanceContext = createContext<AxiosInstance>(axiosLibrary);

interface RequestInterceptors<T> {
  onFulfilled?:
    | ((value: AxiosRequestConfig<T>) => AxiosRequestConfig<T> | Promise<AxiosRequestConfig<T>>)
    | undefined;
  onRejected?: ((error: AxiosError) => AxiosError<T> | Promise<AxiosError<T>>) | undefined;
}

interface ResponseInterceptors<T> {
  onFulfilled?:
    | ((value: AxiosResponse<T>) => AxiosResponse<T> | Promise<AxiosResponse<T>>)
    | undefined;
  onRejected?: ((error: AxiosError) => AxiosError<T> | Promise<AxiosError<T>>) | undefined;
}

interface HttpProviderProps<T> {
  children: ReactElement | ReactElement[] | ReactNode;
  requestInterceptors: RequestInterceptors<T>;
  responseInterceptors: ResponseInterceptors<T>;
  baseUrl?: string;
  options?: RequestOptionsOverride;
}

/**
 * Http provider custom based on axios
 * @param {HttpProviderProps<T>} root the interface of the object
 * @param {ReactElement | ReactElement[] | ReactNode} children the children prop
 * @param {RequestInterceptors<T>} requestInterceptors the functions of request interceptors
 * @param {ResponseInterceptors<T>} responseInterceptors the functions of response interceptors
 * @param {RequestOptionsOverride | undefined} options the general options
 * @param {string | undefined} baseUrl the base url
 * @returns {ReactElement} the context for axios hooks
 */
export const HttpProvider = <T,>({
  children,
  requestInterceptors,
  responseInterceptors,
  options = defaultOptions,
  baseUrl = '',
}: HttpProviderProps<T>): ReactElement => {
  const axios = useMemo(() => {
    return axiosLibrary.create({
      ...options,
      baseURL: baseUrl,
    });
  }, [baseUrl, options]);

  // Add a request and response interceptor
  useEffect(() => {
    axios.interceptors.request.use(requestInterceptors.onFulfilled, requestInterceptors.onRejected);

    axios.interceptors.response.use(
      responseInterceptors.onFulfilled,
      responseInterceptors.onRejected
    );
  }, []);

  return <AxiosIntanceContext.Provider value={axios}>{children}</AxiosIntanceContext.Provider>;
};

HttpProvider.displayName = 'Http provider';
